// src/components/layout/ClientAdminBreadcrumb.tsx
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ChevronRight } from "lucide-react";
import { Fragment } from "react";

interface BreadcrumbSegment {
  label: string;
  href?: string;
}

interface ClientAdminBreadcrumbProps {
  /** The client/organization name to show as the root breadcrumb (e.g., "World Bank Group") */
  clientName: string;
  /** Additional segments after the client name */
  segments?: BreadcrumbSegment[];
}

/**
 * Persistent breadcrumb navigation for client_admin role.
 * Shows: {ClientName} > {SubOrg} > Portfolios > {Portfolio} > Assessments > {Assessment}
 */
export function ClientAdminBreadcrumb({ clientName, segments = [] }: ClientAdminBreadcrumbProps) {
  // Build breadcrumb with client name as root
  const breadcrumbSegments: BreadcrumbSegment[] = [
    { label: clientName, href: "/client/sub-orgs" },
    ...segments,
  ];

  // Don't render if only the root segment exists
  if (breadcrumbSegments.length <= 1) {
    return null;
  }

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        {breadcrumbSegments.map((segment, index) => {
          const isLast = index === breadcrumbSegments.length - 1;

          return (
            <Fragment key={index}>
              <BreadcrumbItem>
                {isLast || !segment.href ? (
                  <BreadcrumbPage>{segment.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={segment.href}>{segment.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && (
                <BreadcrumbSeparator>
                  <ChevronRight className="h-4 w-4" />
                </BreadcrumbSeparator>
              )}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
